import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '@/src/modules/components/Icons';
import { MarketingReveal } from '@/src/modules/components/marketing/MarketingReveal';

interface ContactItem {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}

export function Company() {

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) || 'about';

      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="flex flex-col w-full bg-surface-lowest min-h-screen">

      <div className="flex-1">
        {/* About Section */}
        <section id="about" className="py-24 px-6 border-b border-outline-variant/20">
          <MarketingReveal className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-on-surface mb-6">About InviteCloud</h2>
            <div className="space-y-4 text-on-surface-variant leading-relaxed">
              <p>
                InviteCloud is a digital invitation platform designed to make creating and sharing digital invitations, video experience templates, and guest RSVPs effortless and extraordinary. Founded in June 2026, we believe every celebration deserves an invitation as memorable as the event itself.
              </p>
              <p>
                Our mission is to empower hosts, event planners, and creative partners to design and share beautiful, web-ready invitation experiences within minutes.
              </p>
              <p>
                From intimate family celebrations to large-scale weddings and cultural events, InviteCloud brings together interactive design, rich media, and seamless RSVP management for every celebration.
              </p>
            </div>
          </MarketingReveal>
        </section>

        {/* Privacy Policy Section */}
        <section id="privacy-policy" className="py-20 px-6 border-b border-outline-variant/20">
          <MarketingReveal className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-on-surface mb-4">Privacy Policy</h2>
            <p className="text-on-surface-variant leading-relaxed mb-6">
              We respect your privacy and are committed to protecting personal data collected on the InviteCloud platform. Telemetry and aggregated analytics capture zero personally identifiable information (PII).
            </p>
            <div>
              <Link
                to="/privacy-policy"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-outline-variant/30 bg-surface hover:bg-surface-low text-primary font-semibold text-sm transition-colors"
              >
                Read our full Privacy Policy &rarr;
              </Link>
            </div>
          </MarketingReveal>
        </section>

        {/* Terms of Service Section */}
        <section id="terms" className="py-20 px-6 border-b border-outline-variant/20">
          <MarketingReveal className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-on-surface mb-4">Terms of Service</h2>
            <p className="text-on-surface-variant leading-relaxed mb-6">
              Our Terms of Service govern platform usage, digital invitation delivery, host purchases, partner prepaid credits, and acceptable use under Indian jurisdiction.
            </p>
            <div>
              <Link
                to="/terms"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-outline-variant/30 bg-surface hover:bg-surface-low text-primary font-semibold text-sm transition-colors"
              >
                Read our full Terms of Service &rarr;
              </Link>
            </div>
          </MarketingReveal>
        </section>


        {/* Contact Section */}
        <section id="contact" className="py-24 px-6">
          <MarketingReveal className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-on-surface mb-6">Get in Touch</h2>
            <p className="text-lg text-on-surface-variant mb-8">
              Have questions? We'd love to hear from you. Contact us anytime.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-xl mx-auto text-left">
              <div className="p-6 rounded-2xl bg-surface border border-outline-variant/30 flex flex-col gap-2">
                <span className="text-sm font-semibold text-primary">Founder & Executive</span>
                <a href="mailto:founder@invitecloud.in" className="text-base font-bold text-on-surface hover:text-primary transition-colors">
                  founder@invitecloud.in
                </a>
                <p className="text-xs text-on-surface-variant">For partnerships, strategic inquiries, and executive support.</p>
              </div>

              <div className="p-6 rounded-2xl bg-surface border border-outline-variant/30 flex flex-col gap-2">
                <span className="text-sm font-semibold text-primary">Customer Support</span>
                <a href="mailto:support@invitecloud.in" className="text-base font-bold text-on-surface hover:text-primary transition-colors">
                  support@invitecloud.in
                </a>
                <p className="text-xs text-on-surface-variant">For platform help, billing, and invitation support.</p>
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to="/dashboard/help"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors font-semibold shadow-md"
              >
                <Icons.Help size={20} />
                Help & Support Dashboard
              </Link>
            </div>
          </MarketingReveal>
        </section>
      </div>
    </div>
  );
}
